import { useEffect, useRef, useState } from 'react';

/**
 * El `key` que remonta la pantalla hospedada al cambiar de marca —
 * ADR-2026-08-03-cambiar-de-marca-remonta-la-pantalla.
 *
 * Cada pantalla resolvía por su cuenta qué hacer con `atlas:brand-changed`, y la mayoría
 * no hacía nada: la marca cambiaba en la sidebar y a la derecha seguía la información de
 * la anterior hasta navegar a otro lado y volver. En vez de sumarle un listener a cada
 * una, el shell le cambia la `key` al contenido: la pantalla se desmonta y se monta de
 * nuevo, así que vuelve a correr TODOS sus fetch de montaje con la marca nueva, sepa o no
 * del evento.
 *
 * Vive en un hook porque son DOS shells y sólo el de escritorio lo tenía
 * (ADR-2026-09-13-el-cambio-de-marca-no-llega-al-chat): en mobile, donde el switcher está
 * disponible desde cualquier pantalla, el contenido se quedaba en la marca anterior.
 *
 * Se cuentan CAMBIOS, no valores: al arrancar, `marcaActivaId` pasa de `null` al id real
 * cuando resuelve el fetch de marcas, y usar ese valor de `key` remontaría la pantalla una
 * vez en cada carga —doble fetch de todo, y un parpadeo—. La primera resolución sólo se
 * anota.
 *
 * `transicion` es para el shell que acompaña el remonte con un fade: `alEmpezar` corre en
 * cuanto se detecta el cambio, el remonte espera `retrasoMs` y después corre `alTerminar`.
 * Sin eso el contenido nuevo aparece de golpe encima del viejo. Se lee por ref para que el
 * efecto dependa sólo de la marca y no de la identidad del objeto.
 */
export function useNonceDeMarca(
  marcaActivaId: string | null,
  transicion?: { retrasoMs: number; alEmpezar: () => void; alTerminar: () => void },
): number {
  const marcaPrevia = useRef<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const transicionRef = useRef(transicion);
  transicionRef.current = transicion;

  useEffect(() => {
    if (!marcaActivaId) return;
    if (marcaPrevia.current === null) {
      marcaPrevia.current = marcaActivaId;
      return;
    }
    if (marcaPrevia.current === marcaActivaId) return;
    marcaPrevia.current = marcaActivaId;
    const t = transicionRef.current;
    if (!t) {
      setNonce((n) => n + 1);
      return;
    }
    t.alEmpezar();
    const id = window.setTimeout(() => {
      setNonce((n) => n + 1);
      t.alTerminar();
    }, t.retrasoMs);
    return () => window.clearTimeout(id);
  }, [marcaActivaId]);

  return nonce;
}
